import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, CheckCircle2, ArrowRight } from "lucide-react";
import { Patients } from "@/lib/api";
import { roundScore, scoreDelta } from "@/lib/scoreFormat";

const SEG_PT = {
  feet: "Pés", knees: "Joelhos", pelvis: "Bacia",
  spine: "Coluna", shoulders: "Ombros", cervical: "Cervical",
  scapula: "Omoplatas",
};

/** Linguagem não-diagnóstica — frases derivadas SÓ de delta de score por segmento. */
const IMPROVEMENT_PHRASES = {
  feet:      "Melhor alinhamento dos pés",
  knees:     "Maior simetria nos joelhos",
  pelvis:    "Melhoria do alinhamento da bacia",
  spine:     "Melhor alinhamento da coluna",
  shoulders: "Redução da projeção anterior dos ombros",
  cervical:  "Melhor alinhamento cervical",
  scapula:   "Maior estabilidade das omoplatas",
};

function ScoreBar({ value, prev = null, accent = "sky" }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const prevPct = prev != null ? Math.max(0, Math.min(100, prev)) : null;
  const col = pct >= 80 ? "#34d399" : pct >= 60 ? "#fbbf24" : "#f43f5e";
  return (
    <div className="relative h-2 rounded-full bg-slate-800/80 overflow-hidden">
      {/* marca subtil onde estava na avaliação anterior */}
      {prevPct != null && (
        <div
          className="absolute top-0 bottom-0 w-[2px] bg-slate-500/60"
          style={{ left: `${prevPct}%` }}
          aria-hidden
        />
      )}
      <div
        className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out"
        style={{ width: `${pct}%`, background: col, boxShadow: `0 0 8px ${col}` }}
      />
    </div>
  );
}

function Sparkline({ scores }) {
  // SVG simples: linha + pontos. Sem libs.
  if (!scores || scores.length < 2) return null;
  const W = 280, H = 56, pad = 6;
  const min = Math.min(...scores) - 2;
  const max = Math.max(...scores) + 2;
  const span = Math.max(1, max - min);
  const stepX = (W - pad * 2) / (scores.length - 1);
  const points = scores.map((s, i) => {
    const x = pad + i * stepX;
    const y = H - pad - ((s - min) / span) * (H - pad * 2);
    return [x, y];
  });
  const path = points.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(" ");
  const last = scores[scores.length - 1];
  const lastCol = last >= 80 ? "#34d399" : last >= 60 ? "#fbbf24" : "#f43f5e";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" data-testid="evolution-sparkline">
      <path d={path} fill="none" stroke="rgba(56,189,248,0.6)" strokeWidth="1.5" />
      {points.map(([x, y], i) => (
        <circle
          key={i} cx={x} cy={y}
          r={i === points.length - 1 ? 3.5 : 2}
          fill={i === points.length - 1 ? lastCol : "rgba(148,163,184,0.7)"}
        />
      ))}
    </svg>
  );
}

/**
 * EvolutionInsights — card único que aparece SÓ se houver avaliação anterior analisada
 * para o mesmo paciente. Combina:
 *   - Delta de score global (Fase 1)
 *   - Lista "o que melhorou" gerada por regras simples (Fase 2)
 *   - Barras por segmento com marca da avaliação anterior (Fase 3)
 *   - Mini timeline + sparkline (Fases 4 e 5)
 *
 * Modos:
 *   - Standalone (terapeuta): passa-se `assessment`; o componente busca via Patients.assessments(...)
 *   - Embedded (portal público): passa-se também `previousOverride` (objeto) e `historyOverride` (array).
 *     Nesse caso, NÃO se faz chamada à API protegida.
 *
 * Também expõe prop `compact` para variações mobile-first em modo portal.
 */
export default function EvolutionInsights({ assessment, previousOverride = null, historyOverride = null, hideDeepLink = false, neutralLanguage = false }) {
  const [list, setList] = useState(null);
  const usingOverride = previousOverride != null || (historyOverride && historyOverride.length > 0);

  useEffect(() => {
    if (usingOverride) { setList(null); return; }
    if (!assessment?.patient_id || !assessment?.analysis) { setList(null); return; }
    let alive = true;
    (async () => {
      try {
        const all = await Patients.assessments(assessment.patient_id);
        if (alive) setList(all || []);
      } catch { if (alive) setList([]); }
    })();
    return () => { alive = false; };
  }, [assessment?.patient_id, assessment?.id, assessment?.analysis, usingOverride]);

  const { previous, history } = useMemo(() => {
    // Modo override (portal público) — usa diretamente os dados injetados
    if (usingOverride) {
      const hist = (historyOverride || []).filter(h => h.global_score != null);
      // garantir ordem ASC
      const asc = [...hist].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      return { previous: previousOverride, history: asc };
    }
    if (!list) return { previous: null, history: [] };
    const analysed = list.filter(x => x.analysis?.global_score != null);
    const asc = [...analysed].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const idx = asc.findIndex(x => x.id === assessment.id);
    const prev = idx > 0 ? asc[idx - 1] : null;
    return { previous: prev, history: asc };
  }, [usingOverride, previousOverride, historyOverride, list, assessment?.id]);

  if (!assessment?.analysis || !previous) return null;

  // Single source of truth — sempre arredondado, igual ao Score Ring e ao PDF.
  const curr = roundScore(assessment.analysis.global_score);
  const prev = roundScore(previous.analysis.global_score);
  if (curr == null || prev == null) return null;
  const delta = scoreDelta(assessment.analysis.global_score, previous.analysis.global_score);
  const pctChange = prev > 0 ? +(delta / prev * 100).toFixed(1) : null;

  const trend = neutralLanguage
    ? { txt: "text-sky-300", bg: "bg-sky-500/10", border: "border-sky-500/40", Icon: Minus, label: "Diferença registada" }
    :
    delta > 2 ? { txt: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40", Icon: TrendingUp, label: "Em progresso" } :
    delta < -2 ? { txt: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/40", Icon: TrendingDown, label: "Atenção" } :
    { txt: "text-slate-300", bg: "bg-slate-800/40", border: "border-slate-700", Icon: Minus, label: "Estável" };

  // Segmentos com melhoria > 2 pts → frases "o que melhorou"
  const currSegs = assessment.analysis.segments || {};
  const prevSegs = previous.analysis.segments || {};
  const improvements = Object.keys(currSegs)
    .filter(k => prevSegs[k]?.score != null && currSegs[k]?.score != null)
    .map(k => ({ key: k, delta: +((currSegs[k].score - prevSegs[k].score)).toFixed(1) }))
    .filter(x => x.delta > 2)
    .sort((a, b) => b.delta - a.delta)
    .slice(0, 4);

  const stableButRecovered = !improvements.length && delta > 0
    ? [{ key: "_global", delta }] : [];

  // Timeline de scores (asc) — sempre arredondado para coerência com Score Ring/PDF
  const sparkScores = history
    .map(h => roundScore(h.analysis?.global_score ?? h.global_score))
    .filter(s => s != null);

  // Etiqueta tipo "1ª · 2ª · 3ª avaliação"
  const ord = ["1ª", "2ª", "3ª", "4ª", "5ª", "6ª", "7ª", "8ª", "9ª", "10ª"];
  const currIdxAsc = history.findIndex(h => h.id === assessment.id);

  return (
    <section
      data-testid="evolution-insights"
      className={`rounded-2xl border ${trend.border} ${trend.bg} p-5 sm:p-6 fade-in-up glow-card`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${trend.txt}`}>
            {neutralLanguage ? "Comparação de indicadores" : "Evolução Clínica"}
          </div>
          <div className="font-display text-xl sm:text-2xl text-slate-50 mt-1 flex items-center gap-2 flex-wrap">
            <trend.Icon className="w-5 h-5" strokeWidth={1.8} />
            <span>{delta > 0 ? "+" : ""}{delta} pontos</span>
            {pctChange != null && (
              <span className={`text-xs font-mono ${trend.txt}`}>
                ({pctChange > 0 ? "+" : ""}{pctChange}%)
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            desde {new Date(previous.created_at).toLocaleDateString("pt-PT")} · {trend.label}
          </div>
        </div>

        {/* Mini timeline numérica anterior → atual */}
        <div className="flex items-center gap-3 text-sm shrink-0" data-testid="evolution-timeline">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Anterior</div>
            <div className="font-display text-2xl text-slate-300">{prev}</div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-600" />
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Atual</div>
            <div className={`font-display text-2xl ${trend.txt}`}>{curr}</div>
          </div>
        </div>
      </div>

      {/* O QUE MELHOROU */}
      {!neutralLanguage && (improvements.length > 0 || stableButRecovered.length > 0) && (
        <div className="mt-5 sm:mt-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold mb-2">
            O que melhorou
          </div>
          <ul className="space-y-1.5" data-testid="what-improved">
            {improvements.map(imp => (
              <li key={imp.key} className="flex items-center gap-2 text-sm text-slate-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={1.8} />
                <span className="flex-1">{IMPROVEMENT_PHRASES[imp.key] || `Melhoria em ${SEG_PT[imp.key] || imp.key}`}</span>
                <span className="text-xs font-mono text-emerald-300">+{imp.delta}</span>
              </li>
            ))}
            {stableButRecovered.length > 0 && improvements.length === 0 && (
              <li className="flex items-center gap-2 text-sm text-slate-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={1.8} />
                <span>Melhoria global subtil mantida</span>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* BARRAS POR SEGMENTO (atual com marca anterior) */}
      {neutralLanguage && (
        <div className="mt-5 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="segment-comparison-neutral">
          {Object.keys(currSegs).filter((key) => prevSegs[key]?.score != null && currSegs[key]?.score != null).map((key) => {
            const currentValue = roundScore(currSegs[key]?.score);
            const previousValue = roundScore(prevSegs[key]?.score);
            const change = currentValue == null || previousValue == null ? null : currentValue - previousValue;
            return (
              <div key={key} className="flex items-center justify-between gap-3 rounded-lg bg-slate-900/70 px-3 py-2">
                <span className="text-sm text-slate-300">{SEG_PT[key] || key}</span>
                <span className="text-xs font-mono text-slate-200">
                  {previousValue ?? "—"} → {currentValue ?? "—"} ({change == null ? "—" : `${change > 0 ? "+" : ""}${change}`})
                </span>
              </div>
            );
          })}
        </div>
      )}

      {!neutralLanguage && (
      <div className="mt-5 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="segment-bars">
        {Object.keys(currSegs).map(k => {
          const cv = roundScore(currSegs[k]?.score);
          const pvRaw = prevSegs[k]?.score;
          const pv = pvRaw != null ? roundScore(pvRaw) : null;
          const d = pv != null && cv != null ? cv - pv : null;
          const dCol = d == null ? "text-slate-500" : d > 1 ? "text-emerald-300" : d < -1 ? "text-rose-300" : "text-slate-400";
          return (
            <div key={k} className="flex items-center gap-3" data-testid={`segment-bar-${k}`}>
              <div className="text-[11px] text-slate-300 w-20 sm:w-24 shrink-0">{SEG_PT[k] || k}</div>
              <div className="flex-1 min-w-0"><ScoreBar value={cv ?? 0} prev={pv} /></div>
              <div className="text-[10px] font-mono text-slate-400 w-7 text-right">{cv ?? "—"}</div>
              <div className={`text-[10px] font-mono w-9 text-right ${dCol}`}>
                {d == null ? "—" : (d > 0 ? "+" : "") + d}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* SPARKLINE (≥3 avaliações) */}
      {history.length >= 3 && (
        <div className="mt-5 sm:mt-6">
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sky-300 font-semibold">
              {neutralLanguage ? `Registos — ${history.length} avaliações` : `Trajetória — ${history.length} avaliações`}
            </div>
            <div className="text-[10px] text-slate-500">
              {ord[currIdxAsc] || `${currIdxAsc + 1}ª`} avaliação
            </div>
          </div>
          <Sparkline scores={sparkScores} />
        </div>
      )}

      {/* Link discreto para vista detalhada existente — escondido no portal público */}
      {!hideDeepLink && (
        <div className="mt-4 pt-3 border-t border-slate-800/50">
          <Link
            to={`/patients/${assessment.patient_id}/compare?a=${previous.id}&b=${assessment.id}`}
            className="text-xs text-slate-400 hover:text-sky-300 inline-flex items-center gap-1"
            data-testid="evolution-deep-link"
          >
            Ver comparação detalhada <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </section>
  );
}
